import React, { useState } from 'react';
import { CheckCircle, Sparkles, ChevronDown } from 'lucide-react';
import OverlayCoverageList from '../VendorOverlayPicker/OverlayCoverageList';
import SpendDrillButton from '../components/SpendDrillButton';
import { selectIndicator, selectVendorIndicator, parseVendorDisplay } from '../hooks/useSpendIndicators';
import { PILLARS, MATURITY_LEVELS, CAPABILITY_MATURITY_DESCRIPTIONS } from '../config/ztaArchitectureData';

// ============================================================================
// PILLAR COLUMN COMPONENT
// ============================================================================

/**
 * Render the assigned-vendor row for a capability with per-vendor $ icons.
 * Token sources (in preference order):
 *   1. `savedVendorEntries` — the saved state.vendors array when the user
 *      has edited the capability. Each entry can be "Vendor (Product)".
 *   2. `suggestionTopVendor` / `Product` — when no saved state but the AI
 *      suggestion banner is active. One token.
 *   3. `fallbackText` — the raw default string (e.g. "Okta / SailPoint").
 *      Split on ` / ` and render each as a vendor-only token.
 *
 * Only the tokens whose vendor/product shows up in `spendIndicators.vendors`
 * get a $ next to them — the rest render as plain text.
 */
function renderVendorRow({
  savedVendorEntries,
  fallbackText,
  suggestionTopVendor,
  suggestionTopProduct,
  spendIndicators,
  onVendorDrillIn,
  cap,
  pillar,
}) {
  let tokens = [];
  if (Array.isArray(savedVendorEntries) && savedVendorEntries.length) {
    tokens = savedVendorEntries.map(raw => ({ raw, ...parseVendorDisplay(raw) }));
  } else if (suggestionTopVendor) {
    tokens = [{
      raw: suggestionTopProduct
        ? `${suggestionTopVendor} (${suggestionTopProduct})`
        : suggestionTopVendor,
      vendor: suggestionTopVendor,
      product: suggestionTopProduct || null,
    }];
  } else if (fallbackText) {
    tokens = fallbackText.split(/\s*\/\s*/).filter(Boolean).map(raw => ({
      raw,
      ...parseVendorDisplay(raw),
    }));
  }

  if (tokens.length === 0) {
    return <span className="zta-cap-vendor-text">{fallbackText}</span>;
  }

  return (
    <span className="zta-cap-vendor-text">
      {tokens.map((t, i) => {
        const indicator = selectVendorIndicator(spendIndicators, t.vendor, t.product);
        return (
          <React.Fragment key={`${t.raw}-${i}`}>
            {i > 0 && <span className="zta-cap-vendor-sep"> / </span>}
            <span className="zta-cap-vendor-token">{t.raw}</span>
            {indicator && (
              <SpendDrillButton
                indicator={indicator}
                onClick={() => onVendorDrillIn?.({
                  vendor: t.vendor,
                  product: t.product,
                  scope: 'capability',
                  code: cap.id,
                  name: `${cap.name} · ${t.product || t.vendor}`,
                  pillarName: pillar?.name,
                })}
                label={`${t.vendor} transactions`}
                size={10}
                className="zta-spend-drill--tiny"
              />
            )}
          </React.Fragment>
        );
      })}
    </span>
  );
}

const PillarColumn = ({ pillar, getState, onEdit, onQuickSave, departmentSelected, getCapabilitySuggestion, suggestionModeEnabled, coverageMap = {}, spendIndicators = null, onDrillIn = null, onVendorDrillIn = null }) => {
  const [expandedCap, setExpandedCap] = useState(null);

  const handleMaturityClick = (e, capId, level) => {
    e.stopPropagation();
    if (!departmentSelected) return;

    const currentState = getState(capId);
    onQuickSave(capId, {
      ...currentState,
      maturity: level
    });
  };

  const handleVendorDropdownToggle = (e, capId) => {
    e.stopPropagation();
    setExpandedCap(expandedCap === capId ? null : capId);
  };

  return (
    <div className="zta-pillar" style={{ '--pillar-color': pillar.color }}>
      <div className="zta-pillar-header">
        {pillar.name}
      </div>

      <div className="zta-pillar-caps-label">CAPABILITIES</div>

      <div className="zta-pillar-capabilities">
        {pillar.capabilities.map(cap => {
          const state = getState(cap.id);
          // Only show saved vendors if department selected, otherwise show default cap.vendors
          // Don't fall back to cap.vendors if we have a saved state (even if empty)
          const hasSavedState = state.capability_id || state.vendors !== undefined;
          const displayVendors = departmentSelected && hasSavedState
            ? (state.vendors?.length ? state.vendors.join(' / ') : '')
            : cap.vendors;
          const suggestion = getCapabilitySuggestion ? getCapabilitySuggestion(cap.id) : null;
          const hasSuggestion = suggestionModeEnabled && suggestion?.detected?.length > 0 && !state.vendors?.length;
          const currentMaturity = state.maturity || 'TRAD.';
          const currentMaturityIdx = MATURITY_LEVELS.indexOf(currentMaturity);
          const isExpanded = expandedCap === cap.id;

          // Vendor overlay coverages (sorted strongest first).
          const overlayCoverages = coverageMap[cap.id] || [];
          const hasOverlayCoverage = overlayCoverages.length > 0;
          const primaryOverlay = overlayCoverages[0];

          return (
            <div
              key={cap.id}
              className="zta-capability-wrapper"
              data-overlay-count={hasOverlayCoverage ? overlayCoverages.length : undefined}
              data-overlay-product={primaryOverlay?.productName}
              data-coverage-level={primaryOverlay?.coverageLevel}
            >
              {hasOverlayCoverage && (
                <OverlayCoverageList
                  coverages={overlayCoverages}
                  position="top"
                  spendIndicators={spendIndicators}
                  onDrillIn={(v) => onVendorDrillIn?.({
                    ...v,
                    scope: 'capability',
                    code: cap.id,
                    name: `${cap.name} · ${v.product || v.vendor}`,
                  })}
                />
              )}
              <button
                className={`zta-capability ${cap.critical ? 'zta-capability--critical' : ''} ${hasSuggestion ? 'zta-capability--has-suggestion' : ''} ${hasOverlayCoverage ? 'zta-capability--overlay-active' : ''}`}
                onClick={() => departmentSelected && onEdit({ ...cap, pillarId: pillar.id, pillarName: pillar.name })}
                disabled={!departmentSelected}
              >
                {hasSuggestion && (
                  <span className="zta-cap-suggestion-indicator" title={`Suggested: ${suggestion.topVendor}`}>
                    <Sparkles size={10} />
                  </span>
                )}
                <span className="zta-cap-name">
                  {cap.name}
                </span>
                {departmentSelected && spendIndicators && (
                  <SpendDrillButton
                    indicator={selectIndicator(spendIndicators, 'capabilities', cap.id)}
                    onClick={() => onDrillIn?.({ ...cap, pillarId: pillar.id, pillarName: pillar.name })}
                    label={`${cap.name} transactions`}
                    size={12}
                    className="zta-spend-drill--compact"
                  />
                )}
                {departmentSelected && (
                  <button
                    className="zta-cap-vendor-toggle"
                    onClick={(e) => handleVendorDropdownToggle(e, cap.id)}
                    title="Select vendors"
                  >
                    <ChevronDown size={12} className={isExpanded ? 'rotated' : ''} />
                  </button>
                )}
              </button>

              {/* Vendor Display Row — per-vendor $ icons stamped next to
                  whichever tokens have real spend, so reviewers can spot-
                  check any specific assigned product without opening the
                  edit modal. */}
              {(displayVendors || (hasSuggestion && !displayVendors)) && (
                <div className="zta-cap-vendor-row">
                  {renderVendorRow({
                    savedVendorEntries: departmentSelected && hasSavedState ? state.vendors : null,
                    fallbackText: displayVendors || (hasSuggestion ? suggestion.topVendor : ''),
                    suggestionTopVendor: hasSuggestion ? suggestion.topVendor : null,
                    suggestionTopProduct: hasSuggestion ? suggestion.topProduct : null,
                    spendIndicators,
                    onVendorDrillIn,
                    cap,
                    pillar,
                  })}
                </div>
              )}

              {/* Vendor Dropdown */}
              {isExpanded && departmentSelected && (
                <div className="zta-cap-vendor-dropdown">
                  <div className="zta-cap-vendor-current">
                    <span className="zta-cap-vendor-label">Current Vendors:</span>
                    <span className="zta-cap-vendor-value">{displayVendors || 'None assigned'}</span>
                  </div>
                  {hasSuggestion && (
                    <div className="zta-cap-vendor-suggestion">
                      <Sparkles size={10} />
                      <span>Suggested: <strong>{suggestion.topVendor}</strong></span>
                      {suggestion.topProduct && <span> ({suggestion.topProduct})</span>}
                    </div>
                  )}
                  <button
                    className="zta-cap-vendor-edit"
                    onClick={() => onEdit({ ...cap, pillarId: pillar.id, pillarName: pillar.name })}
                  >
                    Edit Vendors & Details
                  </button>
                </div>
              )}

              {/* Mini Maturity Bar */}
              <div className="zta-cap-maturity-bar">
                {MATURITY_LEVELS.map((level, i) => {
                  const maturityKeyMap = { 'TRAD.': 'traditional', 'INITIAL': 'initial', 'ADVANCED': 'advanced', 'OPTIMAL': 'optimal' };
                  const descriptions = CAPABILITY_MATURITY_DESCRIPTIONS[cap.id] || {};
                  const levelDescription = descriptions[maturityKeyMap[level]] || '';
                  const tooltipText = `${level}: ${levelDescription}`;

                  return (
                    <button
                      key={level}
                      className={`zta-cap-maturity-segment ${i === currentMaturityIdx ? 'active current' : ''}`}
                      data-level={i}
                      onClick={(e) => handleMaturityClick(e, cap.id, level)}
                      disabled={!departmentSelected}
                      title={tooltipText}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="zta-pillar-maturity">
        <div className="zta-maturity-label">OVERALL PILLAR MATURITY</div>
        <MaturityBar pillarId={pillar.id} getState={getState} />
      </div>

      {pillar.ab869Critical && (
        <div className="zta-ab869-badge">AB 869 CRITICAL</div>
      )}
    </div>
  );
};

// ============================================================================
// MATURITY BAR COMPONENT
// ============================================================================

const MaturityBar = ({ pillarId, getState }) => {
  // Calculate average maturity for pillar based on capability states
  const pillar = PILLARS.find(p => p.id === pillarId);
  let totalLevel = 0;
  let count = 0;

  pillar?.capabilities.forEach(cap => {
    const state = getState(cap.id);
    if (state.maturity) {
      const idx = MATURITY_LEVELS.indexOf(state.maturity);
      if (idx >= 0) {
        totalLevel += idx;
        count++;
      }
    }
  });

  const avgLevel = count > 0 ? Math.round(totalLevel / count) : 0;

  return (
    <div className="zta-maturity-bar">
      {MATURITY_LEVELS.map((level, i) => (
        <div
          key={level}
          className={`zta-maturity-segment ${i <= avgLevel ? 'zta-maturity-segment--active' : ''}`}
          data-level={i}
        >
          {level}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// POLICY ENFORCEMENT SECTION - 6 Pillars
// ============================================================================

const ZtaPillars = ({
  selectedDepartment,
  getCapabilityState,
  setEditingCapability,
  saveCapability,
  getCapabilitySuggestion,
  suggestionModeEnabled,
  coverageMap,
  effectiveSpendIndicators,
  openCapabilityDrillIn,
  openVendorDrillIn,
}) => {
  return (
    <section className="zta-layer zta-enforcement">
      <div className="zta-layer-label">POLICY<br/>ENFORCEMENT</div>
      <div className="zta-pillars">
        {PILLARS.map(pillar => (
          <PillarColumn
            key={pillar.id}
            pillar={pillar}
            getState={getCapabilityState}
            onEdit={setEditingCapability}
            onQuickSave={saveCapability}
            departmentSelected={!!selectedDepartment}
            getCapabilitySuggestion={getCapabilitySuggestion}
            suggestionModeEnabled={suggestionModeEnabled}
            coverageMap={coverageMap}
            spendIndicators={effectiveSpendIndicators}
            onDrillIn={openCapabilityDrillIn}
            onVendorDrillIn={openVendorDrillIn}
          />
        ))}
      </div>
    </section>
  );
};

export default ZtaPillars;
